import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { Activity, BarChart3, FileSearch, Layers, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

/** Microsoft JSON date: /Date(1774594800000-0700)/ */
function parseBingDate(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return new Date(raw);
  if (raw instanceof Date) return raw;
  const s = String(raw);
  const m = s.match(/\/Date\((-?\d+)([+-]\d{4})?\)\//);
  if (m) return new Date(parseInt(m[1], 10));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function stripBingMeta(row) {
  if (!row || typeof row !== 'object') return {};
  const { __type, ...rest } = row;
  return rest;
}

function formatCellValue(key, val) {
  if (key === 'Date' || /date$/i.test(key)) {
    const d = parseBingDate(val);
    return d ? format(d, 'MMM d, yyyy') : String(val ?? '—');
  }
  if (typeof val === 'number') return val.toLocaleString();
  if (val == null) return '—';
  return String(val);
}

function CrawlStatsPanel({ rows }) {
  const [showRaw, setShowRaw] = useState(false);

  const { series, latest, chartCodes } = useMemo(() => {
    const raw = (rows || []).map((row) => {
      const rest = stripBingMeta(row);
      const d = parseBingDate(rest.Date);
      return {
        ...rest,
        _ts: d ? d.getTime() : 0,
        _label: d ? format(d, 'MMM d') : '—',
        _fullDate: d ? format(d, 'MMM d, yyyy') : '—',
      };
    });
    const sorted = [...raw].filter((r) => r._ts).sort((a, b) => a._ts - b._ts);
    let last = sorted.length ? sorted[sorted.length - 1] : null;
    if (!last && raw.length) {
      const rest = stripBingMeta(rows[rows.length - 1]);
      last = { ...rest, _fullDate: 'Latest row', _label: '—', _ts: 0 };
    }
    const codes = last
      ? [
          { name: '2xx', value: Number(last.Code2xx) || 0, fill: '#22c55e' },
          { name: '301', value: Number(last.Code301) || 0, fill: '#38bdf8' },
          { name: '302', value: Number(last.Code302) || 0, fill: '#818cf8' },
          { name: '4xx', value: Number(last.Code4xx) || 0, fill: '#f97316' },
          { name: '5xx', value: Number(last.Code5xx) || 0, fill: '#ef4444' },
        ].filter((c) => c.value > 0)
      : [];
    const chartSource = sorted.length ? sorted : raw;
    const chartRows = chartSource.map((r) => ({
      name: r._label,
      fullDate: r._fullDate,
      crawled: Number(r.CrawledPages) || 0,
      errors: Number(r.CrawlErrors) || 0,
    }));
    return { series: chartRows, latest: last, chartCodes: codes.length ? codes : null };
  }, [rows]);

  const displayColumns = useMemo(() => {
    const first = rows?.[0];
    if (!first) return [];
    return Object.keys(first).filter((k) => k !== '__type');
  }, [rows]);

  if (!rows?.length) {
    return <p className="text-sm text-slate-500">No crawl rows returned.</p>;
  }

  return (
    <div className="space-y-6">
      {latest && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              label: 'Crawled pages',
              value: (Number(latest.CrawledPages) || 0).toLocaleString(),
              sub: `on ${latest._fullDate}`,
              icon: Layers,
              tone: 'violet',
            },
            {
              label: 'In index',
              value: (Number(latest.InIndex) || 0).toLocaleString(),
              sub: 'Pages in Bing index',
              icon: FileSearch,
              tone: 'emerald',
            },
            {
              label: 'Crawl errors',
              value: (Number(latest.CrawlErrors) || 0).toLocaleString(),
              sub: 'Recorded issues',
              icon: Activity,
              tone: latest.CrawlErrors > 0 ? 'amber' : 'slate',
            },
            {
              label: 'HTTP errors (4xx+5xx)',
              value: ((Number(latest.Code4xx) || 0) + (Number(latest.Code5xx) || 0)).toLocaleString(),
              sub: 'Last snapshot',
              icon: BarChart3,
              tone: 'rose',
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-white to-slate-50/80 dark:from-slate-950 dark:to-slate-900/80 p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  {card.label}
                </p>
                <card.icon className="w-4 h-4 text-violet-500 opacity-80 shrink-0" />
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1 tabular-nums">{card.value}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{card.sub}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1">Crawled pages over time</h4>
          <p className="text-xs text-slate-500 mb-3">Daily crawl volume from Bing</p>
          <div className="h-[220px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="crawlFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" width={36} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                  }}
                  formatter={(v) => [(v ?? 0).toLocaleString(), 'Crawled']}
                  labelFormatter={(_, p) => p?.[0]?.payload?.fullDate || ''}
                />
                <Area type="monotone" dataKey="crawled" stroke="#7c3aed" strokeWidth={2} fill="url(#crawlFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1">Crawl errors over time</h4>
          <p className="text-xs text-slate-500 mb-3">Spikes worth investigating</p>
          <div className="h-[220px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="errFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" width={36} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  formatter={(v) => [(v ?? 0).toLocaleString(), 'Errors']}
                  labelFormatter={(_, p) => p?.[0]?.payload?.fullDate || ''}
                />
                <Area type="monotone" dataKey="errors" stroke="#ea580c" strokeWidth={2} fill="url(#errFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {chartCodes && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1">HTTP status mix (latest day)</h4>
          <p className="text-xs text-slate-500 mb-3">Response codes from the most recent row</p>
          <div className="h-[200px] w-full max-w-lg min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartCodes} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={32} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip
                  cursor={{ fill: 'rgba(139, 92, 246, 0.06)' }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {chartCodes.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowRaw((s) => !s)}
          className="flex items-center gap-2 text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
        >
          {showRaw ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showRaw ? 'Hide' : 'Show'} full data table
        </button>
        {showRaw && (
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800 max-h-[360px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">
                <tr>
                  {displayColumns.map((k) => (
                    <th
                      key={k}
                      className="p-2.5 text-left font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap"
                    >
                      {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-800/80 hover:bg-slate-50/80 dark:hover:bg-slate-900/50">
                    {displayColumns.map((k) => (
                      <td key={k} className="p-2.5 text-slate-700 dark:text-slate-300 whitespace-nowrap max-w-[220px] truncate" title={String(row[k] ?? '')}>
                        {formatCellValue(k, row[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function QueryStatsPanel({ rows }) {
  const [showRaw, setShowRaw] = useState(false);
  const displayColumns = useMemo(() => {
    const first = rows?.[0];
    if (!first) return [];
    return Object.keys(first).filter((k) => k !== '__type');
  }, [rows]);

  const topQueries = useMemo(() => {
    const qKey = displayColumns.find((c) => /^query$/i.test(c) || c === 'Query');
    if (!qKey) return [];
    const withNum = rows
      .map((row) => {
        const rest = stripBingMeta(row);
        const clicks = Number(rest.Clicks ?? rest.clicks ?? 0);
        const impr = Number(rest.Impressions ?? rest.impressions ?? rest.Impression ?? 0);
        return { ...rest, _sort: clicks * 1000 + impr };
      })
      .sort((a, b) => b._sort - a._sort)
      .slice(0, 12);
    return withNum;
  }, [rows, displayColumns]);

  if (!rows?.length) {
    return <p className="text-sm text-slate-500">No query rows returned.</p>;
  }

  return (
    <div className="space-y-6">
      {topQueries.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3">Top queries</h4>
          <ul className="grid gap-2 sm:grid-cols-2">
            {topQueries.map((row, i) => {
              const q = row.Query ?? row.query ?? `Row ${i + 1}`;
              const clicks = row.Clicks ?? row.clicks;
              const impr = row.Impressions ?? row.impressions ?? row.Impression;
              return (
                <li
                  key={i}
                  className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 px-4 py-3 flex flex-col gap-1"
                >
                  <span className="text-sm font-medium text-slate-900 dark:text-white line-clamp-2">{String(q)}</span>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-600 dark:text-slate-400">
                    {impr != null && <span>{Number(impr).toLocaleString()} impressions</span>}
                    {clicks != null && <span>{Number(clicks).toLocaleString()} clicks</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowRaw((s) => !s)}
          className="flex items-center gap-2 text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
        >
          {showRaw ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showRaw ? 'Hide' : 'Show'} full data table
        </button>
        {showRaw && (
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800 max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">
                <tr>
                  {displayColumns.map((k) => (
                    <th
                      key={k}
                      className="p-2.5 text-left font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap"
                    >
                      {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-800/80 hover:bg-slate-50/80 dark:hover:bg-slate-900/50">
                    {displayColumns.map((k) => (
                      <td key={k} className="p-2.5 text-slate-700 dark:text-slate-300 max-w-[260px] truncate" title={String(row[k] ?? '')}>
                        {formatCellValue(k, row[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BingWebmasterPage() {
  const [activeTab, setActiveTab] = useState('submit');
  const [siteUrl, setSiteUrl] = useState('');
  const [urlToSubmit, setUrlToSubmit] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  /** URL submit response — only shown on Submit tab */
  const [submitResult, setSubmitResult] = useState(null);
  /** Last crawl-stats API payload — only shown on Crawl tab */
  const [crawlResult, setCrawlResult] = useState(null);
  /** Last query-stats API payload — only shown on Query tab */
  const [queryResult, setQueryResult] = useState(null);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus('Submitting...');
    try {
      const { data: res } = await api.post('/bing-webmaster/submit-url', {
        site_url: siteUrl,
        url: urlToSubmit,
      });
      setStatus('Submitted successfully!');
      setSubmitResult(res);
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
      setSubmitResult({ success: false, error: errDetail(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async (type) => {
    if (!siteUrl) {
      setStatus('Please enter a Site URL first');
      return;
    }
    setIsLoading(true);
    setStatus(`Fetching ${type}...`);
    try {
      const { data: res } = await api.get(`/bing-webmaster/${type}`, {
        params: { site_url: siteUrl },
      });
      setStatus('Fetched successfully!');
      if (type === 'crawl-stats') {
        setCrawlResult(res);
      } else if (type === 'query-stats') {
        setQueryResult(res);
      }
    } catch (error) {
      const msg = errDetail(error);
      setStatus(`Error: ${msg}`);
      if (type === 'crawl-stats') {
        setCrawlResult({ success: false, error: msg });
      } else if (type === 'query-stats') {
        setQueryResult({ success: false, error: msg });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Bing Webmaster Tools</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
          Submit URLs and explore crawl and query data with charts (OAuth or API key on the server).
        </p>
      </div>

      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'submit', label: 'Submit URLs' },
          { id: 'crawl', label: 'Crawl Stats' },
          { id: 'query', label: 'Query Stats' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm ${activeTab === t.id ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Site URL</label>
          <input
            type="url"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://example.com"
            className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
          />
        </div>

        {activeTab === 'submit' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">URL to Submit</label>
              <input
                type="url"
                value={urlToSubmit}
                onChange={(e) => setUrlToSubmit(e.target.value)}
                placeholder="https://example.com/new-page"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Submitting…' : 'Submit URL'}
            </button>
          </form>
        )}

        {activeTab === 'crawl' && (
          <button
            type="button"
            onClick={() => fetchStats('crawl-stats')}
            disabled={isLoading}
            className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Fetching…' : 'Fetch Crawl Stats'}
          </button>
        )}

        {activeTab === 'query' && (
          <button
            type="button"
            onClick={() => fetchStats('query-stats')}
            disabled={isLoading}
            className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Fetching…' : 'Fetch Query Stats'}
          </button>
        )}

        {status && (
          <div
            className={`mt-4 p-3 rounded-md text-sm ${
              status.startsWith('Error')
                ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'
            }`}
          >
            {status}
          </div>
        )}

        {activeTab === 'submit' && submitResult && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3">Result</h3>
            {submitResult.success === false && (
              <div className="p-4 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                <span className="font-semibold">Error: </span>
                {submitResult.error}
              </div>
            )}
            {submitResult.success !== false && submitResult.message && (
              <div className="p-4 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 text-sm">
                {submitResult.message}
              </div>
            )}
          </div>
        )}

        {activeTab === 'crawl' && (
          <div className="mt-6">
            {crawlResult ? (
              <>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3">Crawl stats</h3>
                {crawlResult.success === false && (
                  <div className="p-4 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                    <span className="font-semibold">Error: </span>
                    {crawlResult.error}
                  </div>
                )}
                {crawlResult.success !== false && Array.isArray(crawlResult.crawl_stats) && (
                  <CrawlStatsPanel rows={crawlResult.crawl_stats} />
                )}
                {crawlResult.success !== false &&
                  !Array.isArray(crawlResult.crawl_stats) &&
                  !crawlResult.message && (
                  <dl className="divide-y divide-slate-100 dark:divide-slate-800">
                    {Object.entries(crawlResult).map(([k, v]) => (
                      <div key={k} className="flex gap-3 py-1.5">
                        <dt className="text-xs text-slate-500 dark:text-slate-400 w-36 flex-shrink-0">{k}</dt>
                        <dd className="text-xs text-slate-800 dark:text-slate-200 break-all">
                          {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Load crawl data for this site URL with <span className="font-medium text-slate-700 dark:text-slate-300">Fetch Crawl Stats</span>.
              </p>
            )}
          </div>
        )}

        {activeTab === 'query' && (
          <div className="mt-6">
            {queryResult ? (
              <>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3">Query stats</h3>
                {queryResult.success === false && (
                  <div className="p-4 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                    <span className="font-semibold">Error: </span>
                    {queryResult.error}
                  </div>
                )}
                {queryResult.success !== false && Array.isArray(queryResult.query_stats) && (
                  <QueryStatsPanel rows={queryResult.query_stats} />
                )}
                {queryResult.success !== false &&
                  !Array.isArray(queryResult.query_stats) &&
                  !queryResult.message && (
                  <dl className="divide-y divide-slate-100 dark:divide-slate-800">
                    {Object.entries(queryResult).map(([k, v]) => (
                      <div key={k} className="flex gap-3 py-1.5">
                        <dt className="text-xs text-slate-500 dark:text-slate-400 w-36 flex-shrink-0">{k}</dt>
                        <dd className="text-xs text-slate-800 dark:text-slate-200 break-all">
                          {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Load search query data with <span className="font-medium text-slate-700 dark:text-slate-300">Fetch Query Stats</span>.
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
